import {
  GET_USER,
  USER_IS_LOADING,
  PROGRESS_UPLOADING_PERCENTAGE,
  RESET_USER,
} from '../actions/types';

import moment from 'moment-timezone';
import {updateAdmin} from '../../modules/Accounts/AccountsQueries';
import {generateClient, GraphQLResult} from 'aws-amplify/api';
import {UserService} from '@PikaDoApp/npm-pikado-logic';
const client = generateClient();
const userService = new UserService();
const AUTH_TOKEN_KEY = 'pikado_auth_token';
const REFRESH_TOKEN_KEY = 'pikado_refresh_token';
const AUTH_USER_KEY = 'pikado_user';
const PERSISTED_REDUX_KEY = 'persist:root';

export const clearAuthStorage = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(PERSISTED_REDUX_KEY);
};

export const clearAuthState = () => (dispatch: any) => {
  clearAuthStorage();
  dispatch({type: USER_IS_LOADING, payload: false});
  dispatch({type: RESET_USER});
};

const parseJwtPayload = (token: string | null): any | null => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

const isJwtExpired = (token: string | null): boolean => {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return true;

  const expiresAtMs = Number(payload.exp) * 1000;
  const refreshSkewMs = 30 * 1000;

  return Date.now() >= expiresAtMs - refreshSkewMs;
};

const isAdminUser = (user: any): boolean => {
  const roles = Array.isArray(user?.roles) ? user.roles : [];

  return (
    roles.some((role: any) => String(role).toLowerCase() === 'admin') ||
    Boolean(user?.adminProfile || user?.admin)
  );
};

export const setUploadingProgress = (uploadingProgress: number) => {
  console.log('UPLADETEEEE', uploadingProgress);
  return {type: PROGRESS_UPLOADING_PERCENTAGE, payload: uploadingProgress};
};

export const setLastSeenOnline = () => {
  return async (dispatch, getState: any) => {
    let user = getState().user.user;
    console.log('Setting last seen online', user);

    if (user?.admin?.id) {
      const adminResp: GraphQLResult<any> = await client.graphql({
        query: updateAdmin,
        variables: {
          id: user?.admin?.id,
          updatedAt: user?.admin.updatedAt,
          input: {
            lastSeenOnline: moment().utc().format(),
          },
        },
      });

      console.log('user.user', user);
      let userModify = {
        ...user,
        admin: adminResp?.data?.updateAdmin,
      };
      console.log('userModify', userModify);
      dispatch({
        type: GET_USER,
        payload: userModify,
      });
    }
  };
};

export const doSignIn =
  (email: string, password: string) => async (dispatch: any, getState: any) => {
    dispatch({type: USER_IS_LOADING, payload: true});
    try {
      const authResponse = await userService.login({email, password});

      if (!authResponse?.user) {
        throw new Error('Login failed. Please verify your credentials.');
      }

      if (!isAdminUser(authResponse.user)) {
        if (authResponse.refreshToken) {
          await userService.signOut(authResponse.refreshToken).catch(error => {
            console.log('SIGN_OUT_NON_ADMIN_ERROR', error?.message || error);
          });
        }
        clearAuthStorage();
        throw new Error(
          'You are not authorized to access the admin dashboard.',
        );
      }

      if (authResponse.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, authResponse.token);
      }
      if (authResponse.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, authResponse.refreshToken);
      }
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(authResponse.user));

      dispatch({type: USER_IS_LOADING, payload: false});
      dispatch({type: GET_USER, payload: authResponse.user});
    } catch (error: any) {
      console.log('SIGN_IN_ERROR1', error?.message);
      dispatch(isLoadingUser(false));
      alert(
        error?.message?.includes('Not Authorized')
          ? ' You are not authorized to login in'
          : error?.message || String(error),
      );
    }
  };

export const doForgotPassword = (email: string) => async (dispatch: any) => {
  dispatch({type: USER_IS_LOADING, payload: true});

  try {
    const response = await userService.forgotPassword(email);
    alert(
      response?.message ||
        'If this email is registered, a password reset link has been sent.',
    );
  } catch (error: any) {
    console.log('FORGOT_PASSWORD_ERROR', error?.message || error);
    alert(error?.message || String(error));
  } finally {
    dispatch({type: USER_IS_LOADING, payload: false});
  }
};

export const getMe = () => {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    if (state.user?.isLoggedIn && state.user?.user) {
      return;
    }

    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);

    // No session at all -> make sure we are in a clean logged-out state.
    if (!storedToken) {
      dispatch(clearAuthState());
      return;
    }

    dispatch({type: USER_IS_LOADING, payload: true});

    try {
      // Refresh the Keycloak access token first if it is expired / near expiry.
      let activeToken = storedToken;
      if (isJwtExpired(activeToken)) {
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!storedRefreshToken) {
          dispatch(clearAuthState());
          return;
        }

        const response = await userService.refreshToken({
          refreshToken: storedRefreshToken,
        });

        if (!response?.token || !response?.refreshToken) {
          dispatch(clearAuthState());
          return;
        }

        localStorage.setItem(AUTH_TOKEN_KEY, response.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
        activeToken = response.token;
      }

      // Prefer the cached user; otherwise resolve it from the Keycloak token.
      let user: any = null;
      const storedUser = localStorage.getItem(AUTH_USER_KEY);
      if (storedUser) {
        try {
          user = JSON.parse(storedUser);
        } catch {
          user = null;
        }
      }

      if (!user) {
        const keycloakId = parseJwtPayload(activeToken)?.sub;
        if (!keycloakId) {
          dispatch(clearAuthState());
          return;
        }

        user = await userService.getUserByKeycloakId(keycloakId);
        if (user) {
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
        }
      }

      if (!user || !isAdminUser(user)) {
        dispatch(clearAuthState());
        return;
      }

      dispatch({type: GET_USER, payload: user});
      dispatch({type: USER_IS_LOADING, payload: false});
    } catch (error: any) {
      // Invalid / expired token, refresh failure or any lookup error ->
      // clear the session and fall back to the login screen.
      console.log('GET_ME_ERROR', error?.message || error);
      dispatch(clearAuthState());
    }
  };
};

export const resetUser = () => async (dispatch: any) => {
  try {
    dispatch(clearAuthState());
  } catch (error) {
    console.log('reset user-> ', error);
  }
  // dispatch(getUser());
};

export const doSignOut = () => async (dispatch: any) => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  dispatch(clearAuthState());

  try {
    if (refreshToken) {
      await userService.signOut(refreshToken);
    }
  } catch (error: any) {
    console.log('SIGN_OUT_ERROR', error?.message || error);
  }
};

export const isLoadingUser = (isLoading: boolean) => {
  return {
    type: USER_IS_LOADING,
    payload: isLoading,
  };
};
